import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { generateImageUrlFromImd } from '../Util';
import { calculateFanPollState } from './fanPollModel';

const stateColor = (state) => ({
    DRAFT: 'default',
    SCHEDULED: 'info',
    ACTIVE: 'success',
    CLOSED: 'warning',
}[state] ?? 'default');

export default function FanPollPreview({ draft, published }) {
    const [imageUrl, setImageUrl] = useState(null);
    const state = calculateFanPollState(draft, published);

    useEffect(() => {
        let active = true;
        generateImageUrlFromImd(draft?.imageMetadata).then(url => {
            if (active) setImageUrl(url ?? null);
        });
        return () => { active = false; };
    }, [draft?.imageMetadata]);

    return (
        <Paper elevation={5} sx={{ p: 2.5, maxWidth: 560, mx: 'auto' }} aria-label="Non-interactive poll preview">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Typography variant="overline" color="text.secondary">Preview - non-interactive</Typography>
                <Chip size="small" label={state} color={stateColor(state)} />
            </Box>
            {imageUrl ? (
                <Box component="img" src={imageUrl} alt={draft?.imageMetadata?.alt ?? draft?.question ?? ''}
                    sx={{ width: '100%', aspectRatio: '1.905 / 1', objectFit: 'cover', borderRadius: 2, mb: 2 }} />
            ) : (
                <Box sx={{ width: '100%', aspectRatio: '1.905 / 1', bgcolor: 'grey.200', borderRadius: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    <Typography color="text.secondary">Select a poll photo</Typography>
                </Box>
            )}
            {draft?.subject && <Typography variant="subtitle2" color="primary">{draft.subject}</Typography>}
            <Typography variant="h5" sx={{ mb: 2 }}>{draft?.question || 'Your poll question'}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(draft?.options ?? []).map((option, index) => (
                    <Button key={option.id ?? option._key ?? index} variant="outlined" disabled sx={{ justifyContent: 'flex-start' }}>
                        {option.label || `Option ${index + 1}`}
                    </Button>
                ))}
                {draft?.allowSuggestion && <TextField size="small" disabled label="Other answer" />}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
                {draft?.mainTag && <Chip size="small" color="primary" label={draft.mainTag.label} />}
                {(draft?.tagList ?? []).filter(tag => tag?.label !== draft?.mainTag?.label)
                    .map((tag, index) => <Chip size="small" key={`${tag.label}-${index}`} label={tag.label} />)}
            </Box>
            {(draft?.startDate || draft?.endDate) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {draft.startDate ? `Starts ${new Date(draft.startDate).toLocaleString()}` : 'Starts immediately'}
                    {'; '}
                    {draft.endDate ? `ends ${new Date(draft.endDate).toLocaleString()}` : 'no end date'}
                </Alert>
            )}
        </Paper>
    );
}
